import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const NAVY = "#0A234A";

const REGIONS = [
  {
    id: "wa",
    label: "Western Australia",
    corridors: [
      "Kwinana–Cockburn industrial belt",
      "Great Eastern Highway",
      "Coolgardie–Esperance",
      "Eyre Highway",
      "Albany Highway",
      "Great Northern Highway",
    ],
  },
  {
    id: "ab",
    label: "Alberta, Canada",
    corridors: [
      "QEII / Highway 2 (Calgary–Edmonton)",
      "Highway 16 (Yellowhead)",
      "Highway 43 (northwest resource route)",
      "Highway 1 (Trans-Canada)",
      "CentrePort & industrial gateways",
      "Oil-sands haul corridors",
    ],
  },
];

const STATS = [
  { n: "10", l: "New purpose-built hubs" },
  { n: "20", l: "Upgraded rest areas" },
  { n: "2", l: "Countries, one network" },
  { n: "1", l: "App connecting it all" },
];

export const NetworkSection = () => {
  const [id, setId] = useState("wa");
  const active = REGIONS.find((r) => r.id === id) || REGIONS[0];

  return (
    <section
      data-testid="network-section"
      className="relative overflow-hidden bg-white py-20 lg:py-28"
    >
      <div className="relative mx-auto w-full max-w-[1180px] px-6">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-sm font-700 uppercase tracking-[0.18em] text-[#FF5722]">The network</p>
          <h2 className="font-display text-3xl font-700 leading-[1.1] tracking-tight sm:text-5xl" style={{ color: NAVY }}>
            Built across the corridors
            <span className="block text-[#FF5722]">that move the economy.</span>
          </h2>
        </div>

        {/* region toggle */}
        <div className="mb-8 inline-flex gap-1 rounded-2xl bg-white p-1 ring-1 ring-[#FF5722]">
          {REGIONS.map((r) => {
            const on = r.id === id;
            return (
              <button
                key={r.id}
                type="button"
                data-testid={`region-${r.id}`}
                onClick={() => setId(r.id)}
                className={`relative z-10 whitespace-nowrap rounded-xl px-5 py-2.5 text-[14px] font-600 transition-colors duration-300 ${
                  on ? "text-white" : "text-neutral-600 hover:text-neutral-900"
                }`}
              >
                {on && (
                  <motion.span
                    layoutId="region-pill"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    className="absolute inset-0 -z-10 rounded-xl bg-[#FF5722] shadow-[0_10px_20px_-6px_rgba(255,87,34,0.55),inset_0_2px_2px_rgba(255,255,255,0.4),inset_0_-4px_6px_rgba(160,40,0,0.35)]"
                  />
                )}
                <span className="relative">{r.label}</span>
              </button>
            );
          })}
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_1.15fr]">
          {/* corridors */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              transition={{ duration: 0.5, ease: EASE }}
              className="grid gap-3 sm:grid-cols-2"
            >
              {active.corridors.map((c, i) => (
                <motion.div
                  key={c}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.4, ease: EASE }}
                  className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white px-5 py-4 shadow-[0_14px_30px_-24px_rgba(10,35,74,0.35)]"
                >
                  <MapPin className="h-4 w-4 flex-shrink-0 text-[#FF5722]" />
                  <span className="text-[14px] font-500 text-neutral-700">{c}</span>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* framed aerial */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="overflow-hidden rounded-[24px] border border-black/5 shadow-[0_30px_60px_-30px_rgba(10,35,74,0.4)]"
          >
            <img src="/assets/mega-aerial.jpg" alt="FleetPods hub aerial" className="h-full w-full object-cover" draggable="false" />
          </motion.div>
        </div>

        {/* stats */}
        <div className="mt-14 grid gap-6 border-t border-black/10 pt-10 sm:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.l}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
            >
              <div className="font-display text-4xl font-800 text-[#FF5722] sm:text-5xl">{s.n}</div>
              <div className="mt-1 text-sm font-500 text-neutral-500">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
